import { useState } from "react";
import { Outlet, NavLink, useLocation, useNavigate, Link } from "react-router-dom";
import { House, Users, Package, List, X, Files, BookOpen, Gear, SignOut } from "@phosphor-icons/react";
import { toast } from "sonner";
import { useSettings } from "@/contexts/SettingsContext";
import { useAuth } from "@/contexts/AuthContext";
import ChatbotWidget from "@/components/ChatbotWidget";

const Logo = () => (
  <Link to="/" className="group overflow-hidden h-9 md:h-20 lg:h-24 block cursor-pointer relative">
    <div className="relative overflow-hidden">
      <img 
        src="https://customer-assets.emergentagent.com/job_client-products/artifacts/2lxo2p5s_Logo%20Gresart.jpg" 
        alt="GRESART - Ceramic Tiles" 
        className="h-28 md:h-48 lg:h-56 w-auto object-contain object-center -mt-9 md:-mt-14 lg:-mt-16 transition-transform duration-500 group-hover:scale-[1.02]"
      />
    </div>
  </Link>
);

// Bottom nav items for mobile (4 main ones)
const bottomNavItems = [
  { path: "/", icon: House, labelKey: "nav_home" },
  { path: "/clients", icon: Users, labelKey: "nav_clients" },
  { path: "/catalog", icon: Package, labelKey: "nav_ppm" },
  { path: "/physical-catalogs", icon: BookOpen, labelKey: "nav_catalogs" },
];

export default function Layout() {
  const location = useLocation();
  const navigate = useNavigate();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const { logout, t } = useSettings();
  const { logout: sessionLogout } = useAuth();
  
  // Check if we're on the home page
  const isHomePage = location.pathname === "/";

  const isPathActive = (path) => {
    if (path === "/") return location.pathname === "/";
    return location.pathname === path || location.pathname.startsWith(path);
  };

  const handleLogout = async () => {
    logout(); // legacy read-only/PIN mode toggle (unrelated feature, kept as-is)
    await sessionLogout(); // real session logout - required now that login is mandatory app-wide
    toast.success("Sessão terminada");
    navigate("/login", { replace: true });
  };

  return (
    <div className="min-h-screen bg-[#FAFAF9] pb-20 md:pb-0 flex flex-col">
      {/* Top Header */}
      <header className={`bg-white border-b border-stone-200 sticky top-0 z-50 no-print flex-shrink-0`}>
        <div className={`flex items-center justify-between px-4 md:px-8 ${isHomePage ? 'py-0 md:py-1' : 'py-0.5 md:py-2'}`}>
          {/* Left: Logo */}
          <div className="flex-shrink-0">
            <Logo />
          </div>
          
          {/* Desktop top nav removed per user request - Logo is now the only element in the top bar; navigation happens via the Home page grid */}
          
          {/* Right: Mobile Menu Button */}
          <div className="flex-shrink-0 flex items-center gap-2 md:gap-4">
            {/* Hidden logout trigger for programmatic access from Dashboard's visible icon.
                Renamed away from "btn-logout" to avoid a duplicate data-testid collision with
                the visible Sair button on the Profile page. */}
            <button
              onClick={handleLogout}
              className="hidden"
              data-testid="btn-header-logout-trigger"
            />

            {/* Mobile "More" Menu Button - Hidden on home page */}
            {!isHomePage && (
            <button 
              className="md:hidden p-2.5 text-stone-600 hover:text-[#009392] hover:bg-stone-100 rounded-lg transition-colors"
              onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
              data-testid="mobile-menu-btn"
              aria-label="Menu"
            >
              {mobileMenuOpen ? <X size={22} weight="bold" /> : <List size={22} weight="bold" />}
            </button>
            )}
            
            {/* Secondary Logo - Gres Panaria */}
            <img 
              src="https://customer-assets.emergentagent.com/job_client-products/artifacts/to674znt_image.png" 
              alt="Gres Panaria Portugal S.A." 
              className="hidden sm:block h-6 md:h-10 lg:h-12 w-auto object-contain"
            />
          </div>
        </div>

        {/* Mobile Dropdown Menu (for Documents and other items) - Hidden on home page */}
        {!isHomePage && mobileMenuOpen && (
          <nav className="md:hidden border-t border-stone-100 bg-white/95 backdrop-blur-xl">
            <NavLink
              to="/documents"
              onClick={() => setMobileMenuOpen(false)}
              className={`flex items-center gap-3 px-4 py-4 border-b border-stone-100 ${
                isPathActive("/documents")
                  ? "text-[#009392] bg-[#009392]/5" 
                  : "text-stone-600"
              }`}
              data-testid="nav-mobile-documentos"
            >
              <Files size={22} weight={isPathActive("/documents") ? "duotone" : "regular"} />
              <span className="font-medium">Documentos</span>
            </NavLink>
            <NavLink
              to="/profile"
              onClick={() => setMobileMenuOpen(false)}
              className={`flex items-center gap-3 px-4 py-4 border-b border-stone-100 ${
                isPathActive("/profile")
                  ? "text-[#009392] bg-[#009392]/5" 
                  : "text-stone-600"
              }`}
              data-testid="nav-mobile-profile"
            >
              <Gear size={22} weight={isPathActive("/profile") ? "duotone" : "regular"} />
              <span className="font-medium">Meu Perfil</span>
            </NavLink>
            <NavLink
              to="/guide"
              onClick={() => setMobileMenuOpen(false)}
              className="flex items-center gap-3 px-4 py-4 text-stone-600"
              data-testid="nav-mobile-guia"
            >
              <BookOpen size={22} />
              <span className="font-medium">Guia do Utilizador</span>
            </NavLink>
          </nav>
        )}
      </header>

      {/* Main Content */}
      <main className={`flex-1 flex flex-col ${isHomePage ? 'p-2 md:p-4' : 'p-2 md:p-6 lg:p-8'}`}>
        <Outlet />
      </main>

      {/* Footer - always visible (including mobile) so the copyright/link is reachable
          without scrolling on short pages. Sits above the fixed mobile bottom nav thanks
          to the outer wrapper's pb-20 on mobile. */}
      <footer className="border-t border-stone-200 bg-white py-2 px-4 md:px-8 no-print flex-shrink-0">
        <div className="flex items-center justify-between text-xs text-stone-400">
          <p>www.gresart.com</p>
          <p>TileHub Gresart</p>
        </div>
      </footer>

      {/* Mobile Bottom Navigation Bar - Hidden on home page */}
      {!isHomePage && (
      <nav className="md:hidden fixed bottom-0 left-0 right-0 z-50 bg-white/80 backdrop-blur-xl border-t border-stone-200/50 safe-area-pb no-print">
        <div className="flex items-center justify-around py-2">
          {bottomNavItems.map((item) => {
            const Icon = item.icon;
            const isActive = isPathActive(item.path);
            
            return (
              <NavLink
                key={item.path}
                to={item.path}
                className={`flex flex-col items-center justify-center gap-0.5 min-w-[64px] py-2 px-3 rounded-xl transition-all active:scale-95 ${
                  isActive 
                    ? "text-[#009392]" 
                    : "text-stone-400"
                }`}
                data-testid={`nav-bottom-${item.path.replace(/\//g, '') || 'home'}`}
              >
                <div className={`p-1.5 rounded-xl transition-colors ${isActive ? "bg-[#009392]/10" : ""}`}>
                  <Icon size={24} weight={isActive ? "duotone" : "regular"} />
                </div>
                <span className={`text-[10px] font-medium ${isActive ? "text-[#009392]" : "text-stone-500"}`}>
                  {t(item.labelKey)}
                </span>
              </NavLink>
            );
          })}
        </div>
      </nav>
      )}

      {/* Chatbot Widget */}
      <ChatbotWidget />
    </div>
  );
}
